import {
  createEmptyMarket,
  createRound,
  normalizeTicketsByProduct,
} from "../init-game";
import type {
  AllocateTicketsAction,
  ConfirmRoundResultAction,
  FishMarketByProduct,
  FishPriceWon,
  FishProductId,
  FishProductMarket,
  FishRoundResult,
  FishShopV2Player,
  FishShopV2State,
  FishTicketsByProduct,
  LockMarketAction,
  RestartGameAction,
  RevealRoundAction,
  SubmitPricesAction,
  UseCheckAction,
  UseSecretAction,
} from "../types";
import { FISH_PRODUCT_IDS, SALES_TICKETS_PER_PLAYER } from "../types";
import { resolveProductPricing } from "../utils/pricing";

function sumTickets(ticketsByProduct: FishTicketsByProduct): number {
  return FISH_PRODUCT_IDS.reduce(
    (total, productId) => total + ticketsByProduct[productId],
    0,
  );
}

function getGarnetAward(totalIncomeWon: number): number {
  if (totalIncomeWon >= 40000) {
    return 2;
  }
  if (totalIncomeWon >= 30000) {
    return 1;
  }
  return 0;
}

function buildProductMarket(
  state: FishShopV2State,
  productId: FishProductId,
): FishProductMarket {
  const sellers = state.players
    .map((player) => ({
      playerId: player.playerId,
      ticketCount:
        state.currentRound.allocations[player.playerId]?.[productId] ?? 0,
    }))
    .filter((seller) => seller.ticketCount > 0);

  return {
    productId,
    sellers,
    soldTicketCount: sellers.reduce(
      (total, seller) => total + seller.ticketCount,
      0,
    ),
  };
}

function buildMarket(state: FishShopV2State): FishMarketByProduct {
  return {
    HAIRTAIL: buildProductMarket(state, "HAIRTAIL"),
    MACKEREL: buildProductMarket(state, "MACKEREL"),
    SQUID: buildProductMarket(state, "SQUID"),
  };
}

function updatePlayer(
  state: FishShopV2State,
  playerId: string,
  update: (player: FishShopV2Player) => FishShopV2Player,
): readonly FishShopV2Player[] {
  return state.players.map((player) =>
    player.playerId === playerId ? update(player) : player,
  );
}

function settleResults(state: FishShopV2State): readonly FishRoundResult[] {
  const hiddenProductIds = new Set(
    Object.values(state.currentRound.secretSelections),
  );
  const results: FishRoundResult[] = [];

  for (const productId of FISH_PRODUCT_IDS) {
    const market = state.currentRound.market[productId];
    if (market.soldTicketCount === 0) {
      continue;
    }

    const sellerPrices = market.sellers.map((seller) => {
      const priceWon = state.currentRound.priceSubmissions[seller.playerId]?.[
        productId
      ] as FishPriceWon;
      return {
        playerId: seller.playerId,
        priceWon,
      };
    });

    const result = resolveProductPricing({
      productId,
      sellerPrices,
      revenueUnitCount: market.soldTicketCount,
      hidden: hiddenProductIds.has(productId),
    });
    if (result) {
      results.push(result);
    }
  }

  return results;
}

function buildRoundIncome(
  playerIds: readonly string[],
  results: readonly FishRoundResult[],
): Readonly<Record<string, number>> {
  const incomeByPlayerId = Object.fromEntries(
    playerIds.map((playerId) => [playerId, 0]),
  );

  for (const result of results) {
    for (const [playerId, payoutWon] of Object.entries(
      result.payoutByPlayerId,
    )) {
      incomeByPlayerId[playerId] =
        (incomeByPlayerId[playerId] ?? 0) + payoutWon;
    }
  }

  return incomeByPlayerId;
}

function finalizeGame(state: FishShopV2State): FishShopV2State {
  const totals = state.players.map((player) => player.totalIncomeWon);
  const allTied = totals.every((total) => total === totals[0]);

  if (allTied) {
    return {
      ...state,
      currentRound: {
        ...state.currentRound,
        phase: "RESTART_READY",
      },
      completedRounds: [...state.completedRounds, state.currentRound],
    };
  }

  const maxIncome = Math.max(...totals);
  const minIncome = Math.min(...totals);
  const winnerIds = state.players
    .filter((player) => player.totalIncomeWon === maxIncome)
    .map((player) => player.playerId);
  const lowestPlayerIds = state.players
    .filter((player) => player.totalIncomeWon === minIncome)
    .map((player) => player.playerId);

  return {
    ...state,
    status: "COMPLETED",
    currentRound: {
      ...state.currentRound,
      phase: "COMPLETED",
    },
    completedRounds: [...state.completedRounds, state.currentRound],
    winnerIds,
    lowestPlayerIds,
    garnetAwardsByPlayerId: Object.fromEntries(
      state.players.map((player) => [
        player.playerId,
        getGarnetAward(player.totalIncomeWon),
      ]),
    ),
  };
}

function advanceAfterRound(state: FishShopV2State): FishShopV2State {
  if (state.currentRound.roundNumber >= state.roundCount) {
    return finalizeGame(state);
  }

  return {
    ...state,
    completedRounds: [...state.completedRounds, state.currentRound],
    currentRound: createRound(state.currentRound.roundNumber + 1),
  };
}

export function applyAllocateTickets(
  state: FishShopV2State,
  action: AllocateTicketsAction,
): FishShopV2State {
  return {
    ...state,
    currentRound: {
      ...state.currentRound,
      allocations: {
        ...state.currentRound.allocations,
        [action.playerId]: normalizeTicketsByProduct(action.ticketsByProduct),
      },
    },
  };
}

export function applyLockMarket(
  state: FishShopV2State,
  _action: LockMarketAction,
): FishShopV2State {
  const market = buildMarket(state);
  const players = state.players.map((player) => {
    const allocatedTickets = state.currentRound.allocations[player.playerId];
    return {
      ...player,
      remainingTickets: player.remainingTickets - sumTickets(allocatedTickets),
    };
  });

  return {
    ...state,
    players,
    currentRound: {
      ...state.currentRound,
      phase: "MARKET_REVEALED",
      market,
    },
  };
}

export function applySubmitPrices(
  state: FishShopV2State,
  action: SubmitPricesAction,
): FishShopV2State {
  return {
    ...state,
    currentRound: {
      ...state.currentRound,
      phase: "PRICE_SUBMISSION",
      priceSubmissions: {
        ...state.currentRound.priceSubmissions,
        [action.playerId]: action.prices,
      },
    },
  };
}

export function applyUseSecret(
  state: FishShopV2State,
  action: UseSecretAction,
): FishShopV2State {
  return {
    ...state,
    players: updatePlayer(state, action.playerId, (player) => ({
      ...player,
      itemStatus: "USED_SECRET",
    })),
    currentRound: {
      ...state.currentRound,
      secretSelections: {
        ...state.currentRound.secretSelections,
        [action.playerId]: action.productId,
      },
    },
  };
}

export function applyUseCheck(
  state: FishShopV2State,
  action: UseCheckAction,
): FishShopV2State {
  return {
    ...state,
    players: updatePlayer(state, action.playerId, (player) => ({
      ...player,
      itemStatus: "USED_CHECK",
      checkSnapshotWon: player.totalIncomeWon,
    })),
  };
}

export function applyRevealRound(
  state: FishShopV2State,
  _action: RevealRoundAction,
): FishShopV2State {
  const productResults = settleResults(state);
  const incomeByPlayerId = buildRoundIncome(
    state.players.map((player) => player.playerId),
    productResults,
  );
  const players = state.players.map((player) => ({
    ...player,
    totalIncomeWon:
      player.totalIncomeWon + (incomeByPlayerId[player.playerId] ?? 0),
  }));

  return {
    ...state,
    players,
    currentRound: {
      ...state.currentRound,
      phase: "ROUND_REVEALED",
      productResults,
      incomeByPlayerId,
      confirmedPlayerIds: [],
    },
  };
}

export function applyConfirmRoundResult(
  state: FishShopV2State,
  action: ConfirmRoundResultAction,
): FishShopV2State {
  const confirmedPlayerIds = [
    ...new Set([...state.currentRound.confirmedPlayerIds, action.playerId]),
  ];
  const confirmedState = {
    ...state,
    currentRound: {
      ...state.currentRound,
      confirmedPlayerIds,
    },
  };

  if (confirmedPlayerIds.length < state.players.length) {
    return confirmedState;
  }

  return advanceAfterRound(confirmedState);
}

export function applyRestartGame(
  state: FishShopV2State,
  _action: RestartGameAction,
): FishShopV2State {
  return {
    ...state,
    status: "IN_PROGRESS",
    players: state.players.map((player) => ({
      ...player,
      remainingTickets: SALES_TICKETS_PER_PLAYER,
      totalIncomeWon: 0,
      itemStatus: "AVAILABLE",
      checkSnapshotWon: null,
    })),
    completedRounds: [],
    currentRound: {
      ...createRound(1),
      market: createEmptyMarket(),
    },
    winnerIds: [],
    lowestPlayerIds: [],
    garnetAwardsByPlayerId: {},
    restartNumber: state.restartNumber + 1,
  };
}
